import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LogOut, Image, FolderOpen, Users, Database } from 'lucide-react';
import ConcreteCalculatorCard from '../components/admin/ConcreteCalculatorCard';

const AdminDashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check auth
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };

  const adminSections = [
    {
      title: 'Galerie',
      description: 'Gérer les images de projets - éditer titres, descriptions et catégories',
      icon: Image,
      path: '/admin/gallery',
      color: 'from-terracotta-500 to-terracotta-700',
      stats: '150+ projets'
    },
    {
      title: 'Groupes de Projets',
      description: 'Organiser les projets en groupes thématiques pour la page Projets',
      icon: FolderOpen,
      path: '/admin/groups',
      color: 'from-clay-500 to-clay-700',
      stats: 'Projets groupés'
    },
    {
      title: 'CRM Panel',
      description: 'Gérer les contacts, rendez-vous et leads - suivi pipeline commercial',
      icon: Users,
      path: '/admin/crm',
      color: 'from-teal-500 to-teal-700',
      stats: 'Leads & RDV'
    },
    {
      title: 'Données CRM (MongoDB)',
      description: 'Gestion technique des collections - CRUD limité, export CSV, audit log',
      icon: Database,
      path: '/admin/mongodb',
      color: 'from-green-500 to-green-700',
      stats: 'DB Management'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-950 py-6 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-terracotta-400 to-clay-400">
              Admin Panel
            </h1>
            <p className="text-slate-400 mt-1">DumaExpertBéton - Tableau de bord administrateur</p>
          </div>
          <Button 
            onClick={handleLogout} 
            variant="outline" 
            className="text-white border-slate-600 hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-200 mb-2">
            Bienvenue dans l'espace d'administration
          </h2>
          <p className="text-slate-400">
            Sélectionnez la section que vous souhaitez gérer
          </p>
        </div>

        {/* Admin Sections Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Concrete Calculator Performance Card */}
          <ConcreteCalculatorCard />
          
          {adminSections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link key={index} to={section.path}>
                <Card className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer group h-full">
                  <CardHeader>
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl text-slate-100 group-hover:text-white transition-colors">
                      {section.title}
                    </CardTitle>
                    <CardDescription className="text-slate-400 mt-2">
                      {section.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{section.stats}</span>
                      <span className="text-terracotta-400 group-hover:translate-x-2 transition-transform">
                        →
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-400">Accès rapide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Link to="/admin/gallery" className="block text-terracotta-400 hover:text-terracotta-300 text-sm">
                  → Gérer les images
                </Link>
                <Link to="/admin/groups" className="block text-clay-400 hover:text-clay-300 text-sm">
                  → Créer un groupe
                </Link>
                <Link to="/admin/crm" className="block text-teal-400 hover:text-teal-300 text-sm">
                  → Voir les leads
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-400">Documentation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 text-sm">
                Besoin d'aide ? Consultez la documentation pour chaque section ou contactez le support.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-400">Statut</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-slate-300 text-sm">Système opérationnel</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
